/* eslint-disable max-lines */
import Swal from "sweetalert2";
import { get_token, getCookie, csrf_headers } from "@/router";
import {append_active_organization_id, } from "@/utils/organization_profile";

const REPORT_MEDIA_TYPES = {
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    mp4: "video/mp4",
    webm: "video/webm",
    ogg: "video/ogg",
};

function build_request_headers(token, include_content_type = false) {
    const headers = Object.assign(
        { Authorization: `Token ${token}` },
        csrf_headers(getCookie("csrftoken")),
    );

    if (include_content_type) {
        headers["Content-Type"] = "application/json";
    }

    return headers;
}

export const basic_incident_report_methods = {
    handle_incident_feed_scroll() {
        if (this.incident_scroll_timeout) {
            clearTimeout(this.incident_scroll_timeout);
        }

        this.incident_scroll_timeout = setTimeout(() => {
            const container = this.$refs.incident_scroll_container;
            if (
                !container ||
                this.incident_active !== "basic" ||
                this.loading ||
                this.incident_loading_more ||
                !this.incident_has_more
            ) return;

            const scroll_bottom = container.scrollTop + container.clientHeight;
            const load_threshold = container.scrollHeight - 100;
            if (scroll_bottom >= load_threshold) {
                this.load_more_incident_reports().catch((error) => {
                    console.error("Failed to load more incident reports", error);
                });
            }
        }, 200);
    },

    async basic_form_scheme() {
        this.form_schema = [
            {
                id: "basic-incident-details",
                name: "Incident Details",
                sub: "Capture the core details for this incident.",
                questions: [
                    {
                        section_question_id: "basic_incident_location",
                        question_text: "Incident Location",
                        question_type: { question_type: "short_text" },
                    },
                    {
                        section_question_id: "basic_incident_description",
                        question_text: "Incident Description",
                        question_type: { question_type: "long_text" },
                    },
                    {
                        section_question_id: "basic_incident_media",
                        question_text: "Attachments",
                        question_type: { question_type: "file" },
                    },
                ],
            },
        ];
        if (!this.form_data || typeof this.form_data !== "object") {
            this.form_data = {};
        }
        this.form_schema.forEach((section) => {
            section.questions.forEach((question) => {
                if (Reflect.has(this.form_data, question.section_question_id)) {
                    return;
                }
                const is_file = question.question_type?.question_type === "file";
                Reflect.set(this.form_data, question.section_question_id, is_file ? [] : "");
            });
        });

        await this.$nextTick();
        this.load_google_map_event("basic_incident_location");
    },
    get_incident_types() {
        return new Promise((resolve, reject) => {
            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/report_incident_management/get_incident_types/`,
                type: "GET",
                xhrFields: { withCredentials: true },
                headers: build_request_headers(get_token(), true),
                success: (response) => {
                    this.entity_types = response.data.map((item) => ({
                        ...item,
                        entity_type_name: String(item.entity_type_name || "")
                            .replace(/[-_]+/g, " ")
                            .replace(/\b\w/g, (letter) => letter.toUpperCase()),
                    }));
                    resolve(response);
                },
                error: reject,
            });
        });
    },
    get_incident_types_by_scope() {
        return new Promise((resolve, reject) => {
            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/report_incident_management/get_incident_types_by_scope/`,
                type: "POST",
                data: JSON.stringify(append_active_organization_id({})),
                xhrFields: { withCredentials: true },
                headers: build_request_headers(get_token(), true),
                success: (response) => {
                    this.entity_types = response.data.map((item) => ({
                        ...item,
                        entity_type_name: String(item.entity_type_name || "")
                            .replace(/[-_]+/g, " ")
                            .replace(/\b\w/g, (letter) => letter.toUpperCase()),
                    }));
                    resolve(response);
                },
                error: reject,
            });
        });
    },
    append_basic_incident_files(formData) {
        const files = this.form_data.basic_incident_media || [];
        files.forEach((file) => {
            if (file instanceof File) {
                formData.append("files", file);
            }
        });
    },
    build_basic_incident_payload() {
        const formData = new FormData();
        const description = this.form_data.basic_incident_description || "";
        const location = this.form_data.basic_incident_location || "";
        formData.append("description", description);
        formData.append("location", location);
        formData.append("incident_type_id", this.selected_type_id);
        if (this.editing_incident_report_id) {
            formData.append("report_id", this.editing_incident_report_id);
            this.incident_report_files_to_delete.forEach((file_id) => {
                formData.append("files_to_delete", file_id);
            });
        }
        this.append_basic_incident_files(formData);
        return formData;
    },
    get_basic_incident_type() {
        return this.entity_types.find((type) => String(type.id) === String(this.selected_type_id)) || null;
    },
    build_basic_incident_entry(created_report = {}) {
        return {
            ...created_report,
            id: created_report.id,
            report_title: "Incident Report",
            description: created_report.description,
            location: created_report.location,
            date_created: created_report.date_created,
            entity_type: this.get_basic_incident_type(),
            status: created_report.status || { status: "Pending Application" },
            assigned_administrators: created_report.assigned_administrators,
        };
    },
    update_basic_incident_entries(created_report, is_editing_report) {
        if (is_editing_report) {
            this.logged_entries = (this.logged_entries || []).map((report) => (
                report.id === created_report.id ? created_report : report
            ));
            return;
        }
        this.logged_entries = [created_report, ...(this.logged_entries || [])];
    },
    reset_basic_incident_form() {
        this.editing_incident_report_id = null;
        this.incident_report_files_to_delete = [];
        this.form_data = {};
        this.selected_type_id = null;
        this.selected_form_id = null;
        this.form_schema = [];
    },
    handle_basic_incident_success(response, is_editing_report) {
        const created_report = this.build_basic_incident_entry(response.data || {});
        const reference_number = response.data?.id || "N/A";
        this.is_submitting = false;
        this.update_basic_incident_entries(created_report, is_editing_report);
        this.reset_basic_incident_form();
        this.hide_incident_modal();
        this.show_incident_submit_success(reference_number, is_editing_report);
    },
    submit_basic_incident() {
        if (!this.validate_incident_required_fields()) {
            return;
        }

        this.is_submitting = true;
        this.feedback = { message: "", color: "" };
        const is_editing_report = !!this.editing_incident_report_id;

        $.ajax({
            url: `${this.base_url}/report_incident_management/${is_editing_report ? "edit_incident_report" : "create_incident_report"}/`,
            type: "POST",
            xhrFields: { withCredentials: true },
            headers: build_request_headers(get_token()),
            data: this.build_basic_incident_payload(),
            processData: false,
            contentType: false,
            success: (response) => {
                this.handle_basic_incident_success(response, is_editing_report);
            },
            error: (xhr) => {
                this.is_submitting = false;
                this.feedback = {
                    message: xhr.responseJSON?.error || "Failed to submit incident.",
                    color: "danger",
                };
            },
            complete: () => {
                this.loading = false;
            },
        });
    },
    get_incident_reports({ load_more = false } = {}) {
        if (
            this.loading ||
            this.incident_loading_more ||
            (load_more && !this.incident_has_more)
        ) {
            return Promise.resolve();
        }

        const page = this.get_incident_reports_page(load_more);
        this.loading = !load_more;
        this.incident_loading_more = load_more;
        return new Promise((resolve, reject) => {
            $.ajax({
                url: `${process.env.VUE_APP_BACKEND_API_URL}/report_incident_management/get_incident_reports/`,
                type: "POST",
                contentType: "application/json",
                data: JSON.stringify({
                    page,
                    page_size: this.incident_page_size || 10,
                }),
                xhrFields: { withCredentials: true },
                headers: build_request_headers(get_token(), true),
                success: (response) => {
                    this.apply_incident_reports_page(response.data, load_more, page);
                    resolve(response);
                },
                error: (error) => {
                    if (load_more) this.incident_has_more = false;
                    reject(error);
                },
                complete: () => {
                    this.loading = false;
                    this.incident_loading_more = false;
                },
            });
        });
    },
    get_incident_reports_page(load_more) {
        return load_more
            ? Number(this.incident_pagination?.page || 1) + 1
            : 1;
    },
    apply_incident_reports_page(response_data = {}, load_more, requested_page) {
        const results = Array.isArray(response_data)
            ? response_data
            : response_data.results || [];

        this.logged_entries = load_more
            ? this.append_unique_incident_reports(results)
            : results;
        this.incident_pagination = Array.isArray(response_data) ? {} : response_data;
        this.update_incident_pagination_state(response_data, results, requested_page);
    },
    append_unique_incident_reports(reports) {
        const current_reports = this.logged_entries || [];
        const existing_ids = new Set(
            current_reports.map(report => String(report.id)),
        );
        const new_reports = reports.filter(
            report => !existing_ids.has(String(report.id)),
        );
        return [...current_reports, ...new_reports];
    },
    update_incident_pagination_state(response_data, results, requested_page) {
        const response_page = Number(response_data.page || requested_page);
        const total_pages = Number(response_data.total_pages || response_page);
        const has_more = response_data.has_more ?? response_page < total_pages;
        this.incident_has_more = Boolean(has_more && results.length > 0);
    },
    load_more_incident_reports() {
        return this.get_incident_reports({ load_more: true });
    },
    get_report_file_type(file, fileName) {
        const file_details = Object(Object(file).file);
        const explicitType = file_details.type;
        if (explicitType) return explicitType;
        const extension = String(fileName).split(".").pop().toLowerCase();
        return Reflect.get(REPORT_MEDIA_TYPES, extension) || "";
    },
    normalize_basic_report_media(file) {
        if (file instanceof File) {
            return this.normalize_basic_local_report_media(file);
        }

        return this.normalize_basic_remote_report_media(file);
    },
    normalize_basic_local_report_media(file) {
        file.display_name = String(file.name).toLowerCase();
        this.add_basic_report_media_preview(file);
        return file;
    },
    add_basic_report_media_preview(file) {
        if (file.previewUrl) return;
        const is_previewable = ["image/", "video/"]
            .some((prefix) => file.type.startsWith(prefix));
        if (!is_previewable) return;
        file.previewUrl = URL.createObjectURL(file);
    },
    normalize_basic_remote_report_media(file) {
        const file_details = Object(Object(file).file);
        const previewUrl = file_details.file_url || "";
        const name = file_details.name || "attachment";
        const type = this.get_report_file_type(file, name);

        return {
            ...Object(file),
            name,
            display_name: String(name).toLowerCase(),
            type,
            previewUrl
        };
    },
    get_basic_report_media(report) {
        return (report?.incident_report_files || [])
            .map((file) => this.normalize_basic_report_media(file));
    },
    build_basic_report_form_data(report) {
        return {
            basic_incident_location: report?.location || "",
            basic_incident_description: report?.description || "",
            basic_incident_media: this.get_basic_report_media(report)
        };
    },
    async start_edit_incident_report(report) {
        this.is_prefilling_basic_incident = true;
        try {
            this.editing_incident_report_id = report?.id;
            this.incident_report_files_to_delete = [];
            this.incident_active = 'basic';
            this.selected_type_id = report?.incident_type?.id;
            await this.basic_form_scheme();
            this.form_data = this.build_basic_report_form_data(report);
            await this.$nextTick();
            this.open_incident_modal();
        } finally {
            this.is_prefilling_basic_incident = false;
        }
    },
    remove_incident_report_from_list(report_id) {
        this.logged_entries = (this.logged_entries || []).filter((report) => report.id !== report_id);
    },
    async delete_incident_report(report) {
        const report_id = report?.id;
        if (!report_id) return;

        const result = await Swal.fire({
            title: 'Are you sure?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            position: 'bottom-end',
            toast: true
        });
        if (!result.isConfirmed) return;

        const payload = new URLSearchParams();
        payload.append("report_id", report_id);
        $.ajax({
            url: `${process.env.VUE_APP_BACKEND_API_URL}/report_incident_management/delete_incident_report/`,
            type: "POST",
            data: payload,
            xhrFields: { withCredentials: true },
            headers: build_request_headers(get_token()),
            processData: false,
            contentType: false,
            success: (response) => {
                if (response.status === 'success') {
                    Swal.fire({
                        title: 'Deleted!',
                        text: 'The incident report has been deleted.',
                        icon: 'success',
                        showConfirmButton: false,
                        timer: 1000,
                        position: 'bottom-end',
                        toast: true
                    });
                    this.remove_incident_report_from_list(report_id);
                }
            }
        });
    },
    can_edit_incident(report){
        const is_admin = this.user_details.user_role === 'admin' || this.user_details.user_role === 'organization admin';
        const is_creator = String(this.user_details.user_id) === String(report.user.id);
        const is_pending = report.incident_status.name === 'pending';
        return (is_admin || is_creator) && is_pending;
    }
};
